import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as analytics from '../../analytics';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import * as selectors from './selectors';
import SessionApi from '../../api/session';

export class SessionSaga {
  private sessionApi: SessionApi;
  constructor(sessionApi: SessionApi) {
    this.sessionApi = sessionApi;

    // HACK: ES5 ts target mangles the `this` binding
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.keys((this as any).__proto__ as SessionSaga).forEach((key) => {
      this[key] = this[key].bind(this);
    });
  }

  private *maybeFetchSessionInfo() {
    const sessionInfoIsLoaded = yield select(selectors.infoIsLoaded);
    if (!sessionInfoIsLoaded) {
      yield put(actions.beginLoadingSessionInfo());
      yield this.fetchSessionInfo();
    }
  }

  private *fetchSessionInfo() {
    try {
      const session = yield call(this.sessionApi.fetchSessionInfo);
      const human = session && session.human;
      analytics.register({ email: human && human.email });
      yield put(actions.receiveSessionInfo(session));
    } catch (e) {
      yield put(actions.errorSessionInfo(e.message));
    }
  }

  private *logIn(action: ReturnType<(typeof actions)['logIn']>) {
    const { email, password, googleIDToken, facebookIDToken } = action.payload;
    try {
      yield put(actions.beginLogIn());
      const logInResponse = yield call(
        this.sessionApi.logIn,
        email,
        password,
        googleIDToken,
        facebookIDToken,
      );

      if (logInResponse && logInResponse.error) {
        yield put(actions.errorLogIn(logInResponse.error));
        analytics.trackEvent('session', 'login', 'success-with-error', {
          email,
          error: logInResponse.error,
        });
      } else {
        yield put(actions.fetchSessionInfo());
        yield put(actions.successLogIn(logInResponse.redirect_to));
      }
      analytics.trackEvent('session', 'login', 'success', { email });
    } catch (e) {
      const response = e.response;
      const errMessage = e.message;

      if (response && response.error) {
        analytics.trackEvent('session', 'login', 'error-with-response', {
          email,
          error: errMessage,
        });
      }

      yield put(actions.errorLogIn(errMessage, response && response.status));
      analytics.trackEvent('session', 'login', 'error', {
        email,
        error: e.message || (response && response.status),
      });
    }
  }

  // NOTE: The api response can be null or
  // { has_generated_password: boolean | null }
  private parseHumanStatus = (
    statusResponse: {
      has_generated_password: boolean;
    } | null,
  ) => {
    const hasAutoGeneratedPassword = !!(statusResponse && statusResponse.has_generated_password);

    // requestHumanStatus returns null when no human exists for the email
    const isNewHuman = statusResponse === null;

    return {
      hasAutoGeneratedPassword,
      isNewHuman,
    };
  };

  private *maybeResetPassword(
    payload: {
      email: string;
      hasAutoGeneratedPassword: boolean;
      isNewHuman: boolean;
      shouldResetPassword: boolean;
    },
    trackEvent: (label: string, opt: object) => void,
  ) {
    if (payload.shouldResetPassword) {
      // Send an email to customer. Backend logic determines whether that email is
      // a "reset password" email or a "setup password" email
      yield call(this.sessionApi.requestResetPassword, payload.email);
      trackEvent('reset-password', payload);
    }
  }

  private *requestPasswordReset(action: ReturnType<(typeof actions)['requestPasswordReset']>) {
    const { email } = action.payload;
    const trackEvent = (label: string, opt: object) =>
      analytics.trackEvent('session', 'reset-password', label, opt);

    try {
      yield put(actions.beginRequestPasswordReset());
      const statusResponse = yield call(this.sessionApi.requestHumanStatus, email);
      const humanStatus = this.parseHumanStatus(statusResponse);
      const payload = { email, ...humanStatus };
      const { hasAutoGeneratedPassword, isNewHuman } = humanStatus;

      yield call(
        this.maybeResetPassword,
        { ...payload, shouldResetPassword: !isNewHuman },
        trackEvent,
      );

      yield put(actions.successRequestPasswordReset(email, hasAutoGeneratedPassword, isNewHuman));
      const successLabel = isNewHuman ? 'no-account' : 'success';
      trackEvent(successLabel, payload);
    } catch (e) {
      const response = e.response;
      const error = e.message;

      if (response) {
        if (response && response.error) {
          yield put(actions.errorRequestPasswordReset(error, response.status));
          trackEvent('error', { email, error });
          return;
        }
      }

      // NOTE: Explicitly set isNewHuman to true because response has returned null
      yield put(actions.successRequestPasswordReset(email, false, true));
      trackEvent('no-account', { email });
      return;
    }
  }

  private *requestHumanStatus(action: ReturnType<(typeof actions)['requestHumanStatus']>) {
    const { email } = action.payload;
    const trackEvent = (label: string, opt: object) =>
      analytics.trackEvent('session', 'request-human-status', label, opt);
    try {
      yield put(actions.beginRequestHumanStatus());
      const statusResponse = yield call(this.sessionApi.requestHumanStatus, email);
      const humanStatus = this.parseHumanStatus(statusResponse);
      const payload = { email, ...humanStatus };
      const { hasAutoGeneratedPassword, isNewHuman } = humanStatus;

      yield put(actions.successRequestHumanStatus(email, hasAutoGeneratedPassword, isNewHuman));
      const successLabel = isNewHuman ? 'no-account' : 'success';
      trackEvent(successLabel, payload);
    } catch (e) {
      const response = e.response;
      const error = e.message;

      if (response && response.error) {
        yield put(actions.errorRequestHumanStatus(error, response.status));
        trackEvent('error', { email, error });
        return;
      }

      // NOTE: Explicitly set isNewHuman to true because response has returned null
      yield put(actions.successRequestHumanStatus(email, false, true));
      trackEvent('no-account', { email });
      return;
    }
  }

  private *register(action: ReturnType<(typeof actions)['register']>) {
    const {
      email,
      name,
      password,
      phone,
      googleIDToken,
      facebookIDToken,
      passwordlessToken,
      hasGeneratedPassword,
    } = action.payload;
    try {
      yield put(actions.beginRegister());
      const registerResponse = yield call(
        this.sessionApi.register,
        email,
        name,
        password,
        phone,
        googleIDToken,
        facebookIDToken,
        passwordlessToken,
        hasGeneratedPassword,
      );
      if (registerResponse && registerResponse.error) {
        throw new Error(registerResponse.error);
      }
      if (registerResponse && registerResponse.errors) {
        const errorField = Object.keys(registerResponse.errors)[0];
        const errorsArray = registerResponse.errors[errorField];
        if (Array.isArray(errorsArray)) {
          throw Error(
            `That ${errorField.replace('primary_phone', 'phone number').replace('_', ' ')} ${
              errorsArray[0]
            }.`,
          );
        }
      }
      yield put(actions.fetchSessionInfo());
      yield put(actions.successRegister());
      analytics.trackEvent('session', 'register', 'success', { email });
    } catch (e) {
      yield put(actions.errorRegister(e.message));
      analytics.trackEvent('session', 'register', 'error', { email, error: e.message });
    }
  }

  private *maybeLogOut() {
    try {
      const loggedIn = yield select(selectors.loggedIn);
      if (loggedIn) {
        yield call(this.sessionApi.logOut);
      }
      yield put(actions.loggedOut());
    } catch (e) {
      yield put(actions.errorSessionInfo(e.message));
    }
  }

  *saga() {
    yield all([
      takeLatest(actionTypes.MAYBE_FETCH_SESSION_INFO, this.maybeFetchSessionInfo),
      takeLatest(actionTypes.FETCH_SESSION_INFO, this.fetchSessionInfo),
      takeLatest(actionTypes.LOG_IN, this.logIn),
      takeLatest(actionTypes.REGISTER, this.register),
      takeLatest(actionTypes.MAYBE_LOG_OUT, this.maybeLogOut),
      takeLatest(actionTypes.REQUEST_HUMAN_STATUS, this.requestHumanStatus),
      takeLatest(actionTypes.REQUEST_RESET_PASSWORD, this.requestPasswordReset),
    ]);
  }
}

export default SessionSaga;
